import { AlertTitle, Box, Button, Collapse, List, ListItem, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { GetIterativeTrainingData, HideIterativeTrainingInstructions, RegisterIterativeTrainingVote } from '../../../services/RegistrationService';
import Alert from '@mui/material/Alert';
import ListItemText from '@mui/material/ListItemText';
import LinearProgress from '@mui/material/LinearProgress';
import Paper, {PaperProps} from '../../../components/Paper';
import { sanitizeColour, sanitizeRelevance } from '../../../utils/apiUtils';
import Grid from '@mui/material/Grid';
import { roundRelevance } from '../../../utils/relevanceUtils';
import BoxDefaultContent from '../../../components/box/BoxDefaultContent';
import BoxSection from '../../../components/box/BoxSection';
import BreakpointUtils from '../../../utils/BreakpointUtils';
import { RotateRight, ThumbUp } from '@mui/icons-material';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { common, grey } from '@mui/material/colors';
import {useNavigate} from "react-router-dom";
import {GetMaintenanceData} from "../../../services/AuthService";

interface Prediction {
  score: number,
  title: string,
}

interface IterativeTrainingProps {
  stepperUpdate?: () => void;
}

export default function IterativeTrainig(props: IterativeTrainingProps) {
  const [loading, setLoading] = useState<boolean>(true);
  const [showInstructions, setShowInstructions] = useState<boolean>(true);
  const [paper, setPaper] = useState<PaperProps>();
  const [positiveVotes, setPositiveVotes] = useState<string[]>([]);
  const [negativeVotes, setNegativeVotes] = useState<string[]>([]);
  const [predictedPositives, setPredictedPositives] = useState<Prediction[]>([]);
  const [predictedNegatives, setPredictedNegatives] = useState<Prediction[]>([]);
  const [bootstrappedNumber, setBootstrappedNumber] = useState<number>(0);
  const [numberOfPositiveVotes, setNumberOfPositiveVotes] = useState<number>(0);
  const [numberOfNegativeVotes, setNumberOfNegativeVotes] = useState<number>(0);
  const navigate = useNavigate();

  const maintenanceStatus = GetMaintenanceData();
    maintenanceStatus.then(res => {
      // check if in maintenance mode
      if (res.is_maintenance_mode) {
        navigate({
          pathname: '/maintenance',
        });
      }});

  const handleDataUpdate = (response) => {

    // TODO: fix in the backend !! :/
    // guard vs response sometimes returning garbage with no paper_id or paper as undefined
    if (!response || !response.paper || !response.paper.paper_id) {
      console.log('got garbage, retrying..');
      const req = GetIterativeTrainingData();
      req.then(res => handleDataUpdate(res));
      return;
    };

    const responsePaper: PaperProps = {
      paperId: response.paper.paper_id,
      authors: response.paper.shortened_authors,
      title: response.paper.title,
      paperLink: response.paper.url,
      mainText: response.paper.abstract,
      colour: sanitizeColour(response.paper.color),
      relevance: sanitizeRelevance(response.paper.score),
      is_cached: false,
      is_cached_filename: '',
      useScholarInboxPdfServer: false,
      arxivLink: response.paper.url,
    }
    setShowInstructions(!response.hide_instruction_bar)
    setPredictedNegatives(response.neg_seeds);
    setPredictedPositives(response.pos_seeds);
    setPaper(responsePaper);
    setBootstrappedNumber(response.bootstrapped_n);
    setNumberOfNegativeVotes(response.neg_n);
    setNumberOfPositiveVotes(response.pos_n);
    setLoading(false);
  };

  useEffect(() => {
    const req = GetIterativeTrainingData();
    req.then(res => handleDataUpdate(res));
  }, []);

  const hideInstructionsClick = () => {
    const req = HideIterativeTrainingInstructions();
    req.then(res => {
      setShowInstructions(false);
    });
  };

  /* cast a vote
    like = 'true'
    skip = 'skip'
    dislike = 'false'
  */
  const handleVote = (vote: string) => {
    // protect from button mashing
    if (loading) return;
    // cast vote, set loading, get next paper
    const req = RegisterIterativeTrainingVote(paper.paperId, vote);
    if (vote === 'true') {
      positiveVotes.unshift(paper.title);
      setPositiveVotes(positiveVotes.slice(0, 10));
    }
    else if (vote === 'false') {
      negativeVotes.unshift(paper.title);
      setNegativeVotes(negativeVotes.slice(0, 10));
    }
    setPaper(undefined);
    setLoading(true);
    req.then(() => {
      const req = GetIterativeTrainingData();
      req.then(res => handleDataUpdate(res));
    });
  };

  const handleFinishButtonClick = () => {
    props.stepperUpdate();
  }

  const lessThanSmall = BreakpointUtils.lessThanSmall()

  return (
    <BoxDefaultContent>

      {loading && (
        <LinearProgress sx={{ position: 'absolute', top: '0', left: '0', width: '100%', zIndex: 1220 }} color="info" />
      )}

      {showInstructions && (
        <BoxSection>
          <Collapse in={showInstructions}>
            <Alert
              severity="info"
              icon={false}
            >
              <AlertTitle>Iterative Training</AlertTitle>
              You are shown one paper title and abstract at a time and can vote if you like
              <img style={{ width: '1em', marginLeft: '3px', marginRight: '3px' }} src="/image/like.png" alt="Thumbs up" />
              or dislike
              <img style={{ width: '1em', marginLeft: '3px', marginRight: '3px' }} src="/image/dislike.png" alt="Thumbs down" />
              the paper or if you would like to skip
              <img style={{ width: '1em', marginLeft: '3px', marginRight: '3px' }} src="/image/skip.png" alt="Skip" />
              .
              <br />
              After 30-50 votes your personalised recommender is trained well enough, but you may continue voting to improve recommendations further.
            </Alert>
            <Button color="secondary" variant='contained' sx={{ float: 'right', backgroundColor: '#55b776', mt: 1 }} onClick={hideInstructionsClick}>Ok - I read this</Button>
          </Collapse>
        </BoxSection>
      )}

      <Box sx={{ my: '3rem', /*minHeight: '280px'*/ }}>
        <Paper {...{ ...paper, loading: loading, hideControls: true }} />
      </Box>

      <BoxSection>

        { /* Paper options */}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >

          <Box
            onClick={() => handleVote('true')}
            sx={{
              cursor: 'pointer',
              color: grey['500'],
              '@media(hover: hover)': {
                '&:hover': {
                  color: common.black,
                }
              },
            }}
          >
            <ThumbUp sx={{ fontSize: '7rem' }} />
          </Box>

          <Box
            onClick={() => handleVote('false')}
            sx={{
              color: grey['500'],
              cursor: 'pointer',
              mx: 3,
              '@media(hover: hover)': {
                '&:hover': {
                  color: common.black,
                }
              },
            }}
          >
            <ThumbDownIcon sx={{ fontSize: '7rem' }} />
          </Box>

          <Box
            onClick={() => handleVote('skip')}
            sx={{
              cursor: 'pointer',
              color: grey['500'],
              ml: -2,
              '@media(hover: hover)': {
                '&:hover': {
                  color: common.black,
                }
              },
            }}
          >
            <RotateRight sx={{ fontSize: '7rem', }} />
          </Box>

        </Box>

        { /* Info table */}

        <Table size='small' sx={{ mb: 3 }}>
          <TableBody>
            <TableRow
              key={'row0'}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell align='right' sx={{ width: '50%' }}>Bootstrapped papers:</TableCell>
              <TableCell sx={{ width: '50%' }}>{bootstrappedNumber}</TableCell>
            </TableRow>
            <TableRow
              key={'row1'}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell align="right">Positive votes:</TableCell>
              <TableCell>{numberOfPositiveVotes}</TableCell>
            </TableRow>
            <TableRow
              key={'row2'}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell align="right">Negative votes:</TableCell>
              <TableCell>{numberOfNegativeVotes}</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {(props.hasOwnProperty('stepperUpdate')) && (
          <Button
            color='secondary'
            variant='contained'
            sx={{ width: '100px', height: '40px', backgroundColor: '#CC4E49', mb: 4, mt: 1, }}
            title='Continue to the next step: Iterative Training'
            onClick={handleFinishButtonClick}
          >
            Finish
          </Button>
        )}

        {(positiveVotes.length > 0 || negativeVotes.length > 0) && (
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ /*mt: 4*/ }}>
            <Grid item xs={6}>
              <Typography variant='subtitle1' sx={{ textDecoration: 'underline' }}>
                Positive votes
              </Typography>
              <List dense={true}>
                {positiveVotes.map(vote => (
                  <ListItem
                    key={vote}
                    sx={{
                      p: 0,
                    }}
                  >
                    <ListItemText
                      primary={vote}
                      sx={{ m: 0 }} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={6}>
              <Typography variant='subtitle1' sx={{ textDecoration: 'underline' }}>
                Negative votes
              </Typography>
              <List dense={true}>
                {negativeVotes.map(vote => (
                  <ListItem
                    key={vote}
                    sx={{
                      p: 0,
                    }}
                  >
                    <ListItemText
                      primary={vote}
                      sx={{ m: 0 }} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        )}

        {(predictedPositives.length > 0 || predictedNegatives.length > 0) && (
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ /*mt: 4*/ }}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ textDecoration: 'underline' }}>
                Predicted positives
              </Typography>
              <List dense={true}>
                {predictedPositives.map(prediction => (
                  <ListItem
                    key={prediction.title}
                    sx={{
                      // mt: -2
                      p: 0,
                    }}
                  >
                    <ListItemText
                      primary={roundRelevance(prediction.score) + ' | ' + prediction.title}
                      sx={{ m: 0 }} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ textDecoration: 'underline' }}>
                Predicted negatives
              </Typography>
              <List dense={true}>
                {predictedNegatives.map(prediction => (
                  <ListItem
                    key={prediction.title}
                    sx={{
                      // mt: -2
                      p: 0,
                    }}
                  >
                    <ListItemText
                      primary={roundRelevance(prediction.score) + ' | ' + prediction.title}
                      sx={{ m: 0 }} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        )}

      </BoxSection>

    </BoxDefaultContent>
  )
}
